import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth";
import { useState } from "react";
import { cn } from "../lib/utils";
import logoImg from "../assets/logo.png";

// Mirrors NOX dashboard's components/layout/sidebar.tsx — 208px rail, inline
// SVG icons, purple-tinted active nav state, user popup at the foot.

const Icon = {
  Overview: (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
      <path d="M2 10l3-4 2 2 3-4 2 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Releases: (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="7" cy="7" r="1.5" fill="currentColor" />
    </svg>
  ),
  Fans: (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2 12c0-2.2 2.2-3.5 5-3.5s5 1.3 5 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  Agent: (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
      <rect x="2.5" y="3" width="9" height="7" rx="2" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="5.5" cy="6.5" r="0.7" fill="currentColor" />
      <circle cx="8.5" cy="6.5" r="0.7" fill="currentColor" />
      <path d="M7 1.5v1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  Billing: (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
      <rect x="2" y="4" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2 7h10" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  ),
  Settings: (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M7 1.5v2M7 10.5v2M1.5 7h2M10.5 7h2M3.1 3.1l1.4 1.4M9.5 9.5l1.4 1.4M3.1 10.9l1.4-1.4M9.5 4.5l1.4-1.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  Logout: (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
      <path d="M5.5 7H11M9 4.5l2.5 2.5L9 9.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.5 2.5H3a1 1 0 00-1 1v7a1 1 0 001 1h4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
};

interface NavEntry { name: string; icon: React.ReactNode; href: string; end?: boolean; }
const NAV: NavEntry[] = [
  { name: "Overview", icon: Icon.Overview, href: "/app", end: true },
  { name: "Releases", icon: Icon.Releases, href: "/app/releases" },
  { name: "Fans",     icon: Icon.Fans,     href: "/app/fans" },
  // Agent + Billing intentionally hidden — Agent is paused, Billing lives
  // in NOX Private (single subscription covers both products).
  { name: "Settings", icon: Icon.Settings, href: "/app/settings" },
];

export default function AppShell() {
  const { user, active, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="h-full flex">
      <aside className="flex flex-col w-52 min-h-screen bg-card border-r border-border">
        {/* Logo lockup — NOX wordmark above a small SMARTLINKS sub-label */}
        <div className="flex flex-col items-start gap-1 px-4 py-4 border-b border-border/50">
          <img src={logoImg} alt="NOX" className="h-5 w-auto" />
          <span
            className="text-[10px] uppercase tracking-[0.2em]"
            style={{ color: "#c57eff" }}
          >
            Smartlink
          </span>
        </div>

        <nav className="flex-1 px-1.5 py-3 flex flex-col gap-0.5">
          {NAV.map((item) => (
            <NavItem
              key={item.name}
              name={item.name}
              icon={item.icon}
              href={item.href}
              active={isActive(location.pathname, item.href, item.end)}
            />
          ))}
        </nav>

        <div className="px-1.5 pb-3 border-t border-border/50 pt-3 relative">
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute bottom-full left-1.5 right-1.5 mb-1 z-20 rounded-md border border-border bg-card shadow-lg overflow-hidden">
                <div className="px-3 py-2.5 border-b border-border">
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <button
                  onClick={async () => { setMenuOpen(false); await logout(); navigate("/login"); }}
                  className="group flex w-full items-center gap-2.5 px-3 py-2.5 text-sm transition-colors text-muted-foreground hover:text-foreground hover:bg-accent/50"
                >
                  <span className="h-4 w-4 flex-shrink-0">{Icon.Logout}</span>
                  Sign out
                </button>
              </div>
            </>
          )}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex w-full items-center gap-2.5 px-3 py-2 rounded-md transition-colors hover:bg-accent/50"
          >
            <div className="h-6 w-6 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-medium text-muted-foreground uppercase">
                {user?.email?.[0] ?? "?"}
              </span>
            </div>
            <span className="flex-1 text-xs text-muted-foreground truncate text-left">{user?.email}</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        {active ? <Outlet /> : <div className="p-6 text-muted-foreground">No workspace.</div>}
      </main>
    </div>
  );
}

function NavItem({
  name,
  icon,
  href,
  active,
}: {
  name: string;
  icon: React.ReactNode;
  href: string;
  active: boolean;
}) {
  return (
    <NavLink
      to={href}
      className={cn(
        "group flex items-center gap-2.5 px-3 py-2 text-sm font-medium rounded-md transition-colors",
        active
          ? "bg-[rgba(197,126,255,0.08)] text-[#c57eff]"
          : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
      )}
    >
      <span className={cn("flex-shrink-0", active ? "text-[#c57eff]" : "text-muted-foreground group-hover:text-foreground")}>
        {icon}
      </span>
      {name}
    </NavLink>
  );
}

function isActive(pathname: string, href: string, end?: boolean): boolean {
  if (end) return pathname === href;
  return pathname === href || pathname.startsWith(href + "/");
}
